import { asPartialDef } from "../coerce";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

/** Default watch threshold to consider the block completed. */
export const DEFAULT_MIN_WATCH_PCT = 0.9;

export interface VideoDef {
  /** Id of the `video_asset` to play. */
  videoAssetId: string;
  /** Fraction [0,1] that must be watched to complete. Default 0.9. */
  minWatchPct?: number;
}

/**
 * Student response: the REPORTED watch state (fed by
 * `video_watch_progress`). It isn't a gradable "response": the block
 * completes upon crossing `minWatchPct`.
 */
export type VideoResponse = { watchedPct: number };

function clamp01(n: number): number {
  if (typeof n !== "number" || Number.isNaN(n)) {
    return 0;
  }
  return Math.min(1, Math.max(0, n));
}

/**
 * `video` block: plays a `video_asset` and completes by WATCHING (not by
 * response). `interaction: "info"` + `autoGradable: false` (mirrors the
 * non-gradable blocks): anti-fraud knows it doesn't contribute a reliable score; the
 * "grade" only reflects whether the watch threshold was reached.
 */
export const videoBlock: BlockPlugin<VideoDef, VideoResponse> = {
  type: "video",
  interaction: "info",
  autoGradable: false,

  validate(def: unknown): ValidationResult<VideoDef> {
    const d = asPartialDef<VideoDef>(def);
    if (!d) {
      return { ok: false, errors: ["video: definición no es un objeto"] };
    }
    const errors: string[] = [];
    if (typeof d.videoAssetId !== "string" || d.videoAssetId.trim() === "") {
      errors.push("video: `videoAssetId` requerido");
    }
    if (d.minWatchPct !== undefined) {
      if (typeof d.minWatchPct !== "number" || d.minWatchPct < 0 || d.minWatchPct > 1) {
        errors.push("video: `minWatchPct` debe estar en [0,1]");
      }
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      def: {
        videoAssetId: d.videoAssetId ?? "",
        minWatchPct: d.minWatchPct ?? DEFAULT_MIN_WATCH_PCT,
      },
    };
  },

  grade(def: VideoDef, response: VideoResponse | undefined): GradeResult {
    const min = clamp01(def.minWatchPct ?? DEFAULT_MIN_WATCH_PCT);
    if (!response) {
      return { correct: false, score: 0, maxScore: 1 };
    }
    const watched = clamp01(response.watchedPct);
    const complete = watched >= min;
    return { correct: complete, score: complete ? 1 : 0, maxScore: 1 };
  },
};
